// ---------------------------------------------------------------------------
// PER-LOCATION ACCESS MODE — client side of the read-only feature.
//
// A manager or company owner can be linked to several locations with a
// different access mode at each one (Employee wizard -> step 3):
//
//   FULL  everything their roles allow (the historical behaviour, and the
//         default for every account that has never been given a mode)
//   VIEW  read-only: they can see the location but not change it
//
// The backend is the authority. `ca.exponet.kingdom.auth.LocationAccess`
// resolves the mode and `ReadOnlyGateInterceptor` refuses every mutating
// endpoint with HTTP 403 + `readOnly: true`. Everything in THIS file is
// user experience: greying controls out so a viewer sees what they can't
// do instead of discovering it by getting an error.
//
// The mode arrives on `userInfo.accessMode`, recomputed server-side on
// login and on every location switch (the company picker reloads the page,
// which re-runs getUser with the new X-Selected-Company header). There is
// no client-side bookkeeping to keep in sync.
//
// ---------------------------------------------------------------------------
// MAINTENANCE CONTRACT
//
// When you add a new write control, register it under WRITE_SURFACES for
// its feature and wrap it in <WriteGuard> (or pass `disabled={viewOnly}`).
// WRITE_SURFACES is the coverage ledger: it is what tells the next person
// which screens have been swept and which have not.
// ---------------------------------------------------------------------------

export const ACCESS_MODES = {
  FULL: 'FULL',
  VIEW: 'VIEW',
};

// Mirrors LocationAccess.VIEW_ONLY_ELIGIBLE_ROLES on the server. View-only
// is an oversight mode for people who supervise several sites, not a
// general permission tier — line staff, supervisors, reading guys and
// security are single-site by nature and are never put in it.
export const VIEW_ONLY_ELIGIBLE_ROLES = [
  'ROLE_MASTER_ADMIN',
  'ROLE_MANAGER',
  'ROLE_OWNER',
];

// Wizard role keys (employeeRoles.js ROLE_KEYS) whose chips get a
// Full Control / View Only selector. Everyone else is always full control
// at the locations they hold.
export const VIEW_ONLY_ELIGIBLE_ROLE_KEYS = ['owner', 'manager'];

/**
 * True when the user is currently operating a location in read-only mode.
 * Defaults to false for anything unexpected (missing userInfo, missing
 * accessMode, an older cached session) so the UI never grinds to a halt
 * over a field that isn't there — the server still refuses the write.
 */
export function isViewOnly(userInfo) {
  return userInfo?.accessMode === ACCESS_MODES.VIEW;
}

/**
 * Is this wizard role allowed to be given a per-location mode at all?
 */
export function roleKeySupportsViewOnly(roleKey) {
  return VIEW_ONLY_ELIGIBLE_ROLE_KEYS.includes(roleKey);
}

/**
 * The message shown wherever a control is greyed out, and in the toast
 * raised when the server refuses a write we failed to grey out.
 */
export const VIEW_ONLY_MESSAGE =
  'View only at this location — switch to a location where you have full ' +
  'control to make changes.';

export const VIEW_ONLY_SHORT = 'View Only';

// ---------------------------------------------------------------------------
// Toolbar auto-hide
//
// The two shared toolbars — TopToolbar (`menuItems` / onClick) and
// CustomToolbar (`headerButtons` / handler) — render whatever each page
// hands them, so they can drop write actions centrally, but only if they
// can tell which ones write. Titles are the only thing every item
// reliably carries, so they are the key.
//
// These are HIDDEN, not greyed out. An "Add" button that opens a form the
// user can fill in and then can't save is worse than no button at all —
// the refusal has to happen where the user starts, not where they finish.
//
// Read actions (Back, Download PDF, Print, Zoom, Refresh, View Report,
// Audit Log, Restore Customers — which only switches the table to the
// archived view) are deliberately NOT here: a viewer keeps all of them.
// ---------------------------------------------------------------------------

// Titles whose leading word isn't one of the write verbs below, so they
// have to be named explicitly. Verified against every title actually
// passed to the two toolbars — including the ones supplied via constants
// (SAVE, SAVE_GAME, SAVE_MACHINE, ADD_CUSTOMER_TOOLTIP all resolve to
// strings the verb check already catches).
export const WRITE_TOOLBAR_TITLES = [
  'Save',
  'Save changes',
  'Set Cash Left',
  'Manager Settings',
  'Draw Machine', // raffle draw — writes the winner
  'Match Tickets', // /machine/matchTickets
];

const WRITE_TOOLBAR_TITLE_SET = new Set(
  WRITE_TOOLBAR_TITLES.map((t) => t.toLowerCase())
);

// Titles that LOOK like writes to the verb check below but only NAVIGATE
// to another screen. A viewer should still be able to open these — the
// destination has its own read-only guard, so removing the doorway just
// hides information they're allowed to see.
//
// Checked BEFORE the verb list, so an entry here always wins.
export const READ_TOOLBAR_TITLES = [
  // Transaction -> transaction categories. Opens the settings screen,
  // which renders inert via <WriteGuard mode="disable">; the title starts
  // with "Add" purely because that's where you'd add a category.
  'Add description',
];

const READ_TOOLBAR_TITLE_SET = new Set(
  READ_TOOLBAR_TITLES.map((t) => t.toLowerCase())
);

// Leading verbs that mark a toolbar item as a write (or as the doorway
// into an add/edit form). Checked when the title isn't registered above,
// so a page that grows an "Add Machine" button is covered without
// touching this file first. Registering the exact title is still
// preferred — it keeps the behaviour explicit and searchable.
const WRITE_TITLE_VERBS = /^(add|new|create|edit|delete|remove|save|upload|import|assign|reset|start|end|cancel|approve|grant|revoke|send)\b/i;

/**
 * True when a toolbar item mutates data — or opens the form that does —
 * and must therefore be hidden from a view-only user.
 *
 * Accepts items from either shared toolbar: TopToolbar's `menuItems`
 * and CustomToolbar's `headerButtons` share the `title` field.
 */
export function isWriteToolbarItem(menuItem) {
  const title = (menuItem?.title || '').trim();
  if (!title) return false;
  const key = title.toLowerCase();
  if (READ_TOOLBAR_TITLE_SET.has(key)) return false;
  if (WRITE_TOOLBAR_TITLE_SET.has(key)) return true;
  return WRITE_TITLE_VERBS.test(title);
}

/**
 * Drop every write item from a toolbar list when the user is view-only.
 * Returns the list untouched otherwise — including the same array
 * reference, so the `prevProps.menuItems !== this.props.menuItems`
 * checks the toolbars use to reset popper state keep working.
 */
export function filterToolbarItems(items, viewOnly) {
  if (!viewOnly || !Array.isArray(items)) return items;
  return items.filter((item) => !isWriteToolbarItem(item));
}

// ---------------------------------------------------------------------------
// ENDPOINT MAP — the client mirror of LocationAccess.READ_ACTIONS.
//
// Greying buttons out covers what the user can SEE. This map covers what
// the app can DO: AuthUtil consults it before every request and refuses a
// write locally at a view-only location, so a timer, a retry, a keyboard
// shortcut or an unswept control can't fire one. That makes the block
// complete instead of button-by-button.
//
// It is DELIBERATELY the same shape and the same contents as
// `LocationAccess.READ_ACTIONS` on the server. Both must be edited
// together when an endpoint is added — the server is what actually
// enforces (a modified client is not a threat model), and this side
// exists so a viewer gets an instant, explanatory refusal instead of a
// round-trip and a generic error.
//
// DEFAULT-DENY, same as the server: an action absent from a listed
// controller is a write. A controller absent entirely falls through to
// the server, which decides — that way a gap here can never block a read
// the server would have served.
// ---------------------------------------------------------------------------

export const READ_ACTIONS = {
  alert: ['listAlerts'],
  audit: [
    'listReadingSessionAudits',
    'listWeeklyReportAudits',
    'listCustomerPointAudits',
  ],
  bonus: ['list', 'running', 'previewImage', 'bonusList'],
  boost: [
    'list', 'historyPage', 'detail', 'costEstimate', 'audiencePreview',
    'audienceCustomers', 'templateList', 'creditStatus', 'settingsGet',
    'adminGet', 'analytics', 'platformConfigGet',
  ],
  clock: ['loadClockRecords', 'listEmployeeSession'],
  company: [
    'list', 'listAvailableCompanies', 'getCompanyMemberships',
    'getMasterAdmins', 'listLicensePayments', 'viewCompanyLicenseKey',
    'getLicenseHistory', 'getActualMembership', 'getYearlyProjections',
  ],
  customer: [
    'list', 'listArchieveCustomer', 'customerPendingList',
    'customerPointsList', 'customerPendingByUserAndDate', 'getCustomerHistory',
    'getHistory', 'fetchCustomerPoint', 'searchByCustomerName',
    'validateCustomer', 'blacklistSearch', 'pointWatchingByDate',
    'pointWatchingDayEntries', 'pointWatchingEmployeeDays',
    'pointWatchingAlerts', 'previewImage', 'previewVipPhoto',
    'previewPointWatchingImage', 'previewFile',
  ],
  game: ['list'],
  image: ['proxy'],
  job: [
    'index', 'listJobs', 'listJobTriggers', 'listTriggers',
    'listTriggerStates', 'listTriggerState',
  ],
  loan: ['listCustomerLoan', 'listEmployeeLoan', 'listZelleLoan'],
  machine: [
    'list', 'listReading', 'listMachinePicture', 'listAssignTemplates',
    'listDeletedReadingSessions', 'getSessionIds', 'loadStartedSession',
    'getAvailableEmployeeSessions', 'getPreviousInAndOutReading',
    'getLastTwoReadingSessions', 'auditStatus',
  ],
  maintenancePermit: ['current', 'history'],
  note: ['list'],
  onboarding: [
    'status', 'listRequests', 'requestDetails', 'checkUsername', 'invoice',
  ],
  raffle: ['listRaffles'],
  report: [
    'weeklyReport', 'shortAnalysis', 'previewMatchPointReport',
    'previewMachineTicketReport', 'previewMasterReadingReport',
    'previewActiveMembership', 'generateMembership',
    'getManagerSummaryByLocation', 'locationManagerSummary',
    'locationWeekReport', 'locationYearProfitSummary', 'getSessionReports',
    'getAuditLog', 'getOwnerAuditSettings', 'getDuplicateScores',
  ],
  rule: ['list'],
  security: ['getBlacklistCustomers', 'getGateEntries', 'checkStatus'],
  stripe: ['publicConfig', 'payNowStatus', 'listSavedCards', 'quoteFee'],
  team: ['fetchLinkedUsers', 'getRoles', 'getLocationPeople'],
  ticketOut: ['list', 'getPhotoSetting', 'previewPhoto'],
  transaction: ['list', 'listPendingTransfers', 'listCategory', 'previewPhoto'],
  uuid: ['getUUID', 'getUUIDs'],
  verification: ['consentDetails'],
  wallet: ['existing', 'claimDetails', 'downloadPass'],
  account: ['displayname', 'getAllEmployees', 'getOnlineEmployee'],
};

// Always reachable: the auth handshake and self-scoped state that carries
// no location data. Mirrors LocationAccess.ALWAYS_ALLOWED.
export const ALWAYS_ALLOWED_ENDPOINTS = [
  'api:login',
  'api:logout',
  'api:validate',
  'account:getUser',
  'account:getUsers',
  'account:getUserSetting',
  'account:saveUserSetting',
  'company:listAvailableCompanies',
];

// Refused even though the name might read as harmless. Mirrors
// LocationAccess.ALWAYS_BLOCKED — a view-only manager is observing a
// site, not working a shift there, and clock records feed payroll.
export const ALWAYS_BLOCKED_ENDPOINTS = [
  'clock:clockIn',
  'clock:clockOut',
  'clock:manualClockSave',
  'clock:forceClockOut',
  'clock:updateStatus',
  'clock:deleteEmployeeSession',
  'account:processRFID',
  'account:registerRfid',
  'account:deleteRfid',
];

/**
 * Pull `controller` and `action` out of an API URL. Endpoints follow
 * Grails' `/$controller/$action` convention, so the first two path
 * segments are all that matter — trailing segments (`/image/proxy/<id>`,
 * `/wallet/downloadPass/<token>`) and query strings are ignored.
 *
 * Returns null for anything that doesn't parse, which callers treat as
 * "let the server decide".
 */
export function parseEndpoint(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const path = url.split('?')[0].replace(/^https?:\/\/[^/]+/i, '');
    const parts = path.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    return { controller: parts[0], action: parts[1] };
  } catch (e) {
    return null;
  }
}

/**
 * Can a view-only user call this endpoint?
 *
 * Returns true for reads, for the always-allowed set, and — importantly
 * — for any controller this map doesn't know about, so a gap here degrades
 * to the server's decision rather than breaking a legitimate read.
 */
export function isReadEndpoint(url) {
  const parsed = parseEndpoint(url);
  if (!parsed) return true; // unparseable → let the server decide

  const key = `${parsed.controller}:${parsed.action}`;
  if (ALWAYS_BLOCKED_ENDPOINTS.includes(key)) return false;
  if (ALWAYS_ALLOWED_ENDPOINTS.includes(key)) return true;

  const reads = READ_ACTIONS[parsed.controller];
  if (!reads) return true; // unknown controller → let the server decide

  return reads.includes(parsed.action);
}

// ---------------------------------------------------------------------------
// COVERAGE LEDGER
//
// feature -> the write surfaces that have been guarded for view-only.
// Purely descriptive: nothing reads this at runtime. It exists so the
// next person can see at a glance what is covered, and so a code review
// can catch a new write control that was never wrapped.
//
// `central: true` means the guard lives in a shared component and covers
// every page that uses it — no per-page work needed.
// ---------------------------------------------------------------------------

export const WRITE_SURFACES = {
  // Guards that live in shared components and therefore cover every
  // screen that uses them — no per-page work required. Between them the
  // two toolbars carry the "Add" / "Save" affordance on almost every
  // screen in the app, so this is where most of the coverage comes from.
  _shared: {
    central: true,
    guarded: [
      'AuthUtil.guardWrite — refuses every write request app-wide (functional backstop)',
      'TopToolbar — write menuItems removed (21 screens)',
      'CustomToolbar — write headerButtons + the create (+) button removed (17 screens)',
      'SettingsToolbar — Save changes + the create (+) button removed',
      'TwoStepDelete — the row delete button on ~18 screens',
      'DeleteUser — Manage Employees has its own copy of the delete flow '
        + 'rather than using a shared one, so it needs its own guard',
      // NOTE: global/DeleteStepper.jsx is guarded too, but nothing imports
      // it — it is dead code. Guarded anyway so it stays correct if it ever
      // gets wired up; do not count it as coverage for any screen.
    ],
  },
  // Per-screen. Most use <WriteGuard>; a few fold view-only into a gate
  // the screen already had, which is preferable when one exists — it
  // reuses the disabled styling and messaging already built for it.
  customer: {
    guarded: [
      'CustomerTable row: manual points and restore removed',
      'CustomerProfile tab CONTENT inerted; the tab bar stays navigable so a '
        + 'viewer can read every tab. The row edit icon is deliberately left '
        + 'in place — it opens the profile, which is a view.',
      'CustomerForm save',
      'CustomerManualPoint set points',
    ],
  },
  accounting: {
    guarded: [
      'CustomerLoanReport, EmployeeShortReport and ZelleReport: the '
        + 'add-transaction form (inputs + Add button) is inert; the reports '
        + 'and their Download actions stay fully available',
    ],
  },
  accessRequests: {
    guarded: [
      'AccessRequestsPanel — Accept / Accept as View Only / Decline / Revoke '
        + 'and the outgoing Cancel. Deciding who reaches a location is a write '
        + 'even though it lives in the notification drawer, and an owner who '
        + 'only OBSERVES a site must not be able to hand it out. The queue '
        + 'itself stays readable (the list endpoints are in READ_ACTIONS).',
      'EmployeeWizard "Cancel request" on a pending location row',
    ],
  },
  ticketOut: { guarded: ['the entire ticket-entry form (table still shown)'] },
  bonus: { guarded: ['the mobile bonus-claim flow (table still shown)'] },
  note: { guarded: ['add-note button + header bar', 'per-note delete'] },
  boost: {
    guarded: [
      'folded into the existing `sendBlocked` flag, which already gated every '
        + 'messaging action (send / schedule / quick / resend / retry / template create)',
      'credit recharge and Save Settings — purchases and config, not messaging, '
        + 'so not covered by sendBlocked',
      'delete template — had no guard at all before',
    ],
  },
  employeeSession: {
    guarded: [
      'ManagerEditModeToggle returns null — the ONLY way into V2 edit mode, '
        + 'so this closes the edit sheets, add and delete on Shift/Bonus/Raffle at once',
      'audit verdict Flag/Approve on Bonus, Points and Raffle audit modes',
      'session delete + force clock out',
    ],
  },
  reading: {
    guarded: [
      'Start New Session, End Session, Delete/Cancel',
      'the "Open Session #N" buttons — reopening a closed session is a write',
      'the meter grid SAVE button',
    ],
  },
  report: {
    guarded: [
      'folded into canEditReport() — the single gate for per-day fields, '
        + 'manager summary and ATM cash flow',
      'canManageBankBoard() — bank board row edit/delete',
    ],
  },
  settings: {
    guarded: [
      'ManageGame, ManageMachine, AssignGameToMachine, TransactionCategory and '
        + 'ManageRule use <WriteGuard mode="disable">: greyed and inert rather '
        + 'than hidden, because the point of those screens is reading the '
        + 'config. Their Save buttons are already dropped by the toolbar filter.',
      // Worth stating explicitly: on ManageMachine and AssignGameToMachine the
      // guard has to start ABOVE the grid, at the range inputs, so it covers
      // "Generate Machines" / "Generate Assign Templates" too. Those sit
      // outside the grid container and were live on the first pass.
      'ManageMachine "Generate Machines" and AssignGameToMachine '
        + '"Generate Assign Templates" range blocks are inside the same guard',
      'ManageBonus: folded into the screen\'s own readOnly flag, which already '
        + 'retitles the page "View bonus" and inerts every field; the list\'s '
        + 'enable/disable Switch falls back to the lock icon it already shows '
        + 'for rows the user did not create',
      'ManageUsers: Add Employee bar removed; the wizard\'s form content is '
        + 'inerted and its SAVE removed, but the row edit icon and the '
        + 'tab bar/stepper stay so a viewer can open an employee and read '
        + 'their full setup; the row delete button is removed via DeleteUser',
    ],
  },
  transaction: {
    // Row deletes come from the shared TwoStepDelete. The remaining row
    // icons (Show Comment, View Photo, Preview Bonus Image) are reads and
    // are deliberately left alone.
    guarded: [
      'the entire bank / expense entry form (the Bank and Expenses tables '
        + 'still show); row deletes via TwoStepDelete, toolbar via the filter',
    ],
  },
  raffle: { guarded: ['covered by the shared toolbar + TwoStepDelete guards'] },
};
